/**
 * Removing API responses the service worker has kept.
 *
 * Service worker caches live in the browser's Cache Storage, which the page can
 * read and delete directly, so none of this needs the worker's cooperation. That
 * matters because the worker controlling the page may be an older version.
 *
 * Current workers keep only public boards, in caches named
 * `capital-pinball-api-v<version>`. Workers before 2026-09-27 kept every API
 * response, including other members' profiles, membership and payment data and
 * an admin's full member list, in the app shell cache keyed only by URL. Those
 * entries are why logout has to clear this: on a shared device the next person
 * could be shown them.
 */

const API_CACHE_PREFIX = 'capital-pinball-api-';

function isApiUrl(url) {
  try {
    return new URL(url).pathname.startsWith('/api/');
  } catch (error) {
    return false;
  }
}

function cacheStorage() {
  return typeof window !== 'undefined' && window.caches ? window.caches : null;
}

async function deleteApiEntries(cache) {
  const requests = await cache.keys();
  await Promise.all(requests.filter((request) => isApiUrl(request.url)).map((request) => cache.delete(request)));
}

/** API responses older workers left in the app shell cache. Safe to run on every load. */
export async function purgeLegacyApiEntries() {
  const storage = cacheStorage();
  if (!storage) return;
  try {
    const names = await storage.keys();
    await Promise.all(
      names
        .filter((name) => !name.startsWith(API_CACHE_PREFIX))
        .map(async (name) => deleteApiEntries(await storage.open(name)))
    );
  } catch (error) {
    console.warn('Could not clear old saved API data:', error);
  }
}

/** Every kept API response, current and legacy. Run whenever the signed-in person changes. */
export async function clearApiCaches() {
  const storage = cacheStorage();
  if (!storage) return;
  try {
    const names = await storage.keys();
    await Promise.all(
      names.map(async (name) => {
        if (name.startsWith(API_CACHE_PREFIX)) {
          await storage.delete(name);
        } else {
          await deleteApiEntries(await storage.open(name));
        }
      })
    );
  } catch (error) {
    console.warn('Could not clear saved API data:', error);
  }
}
